import { useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { logout } from "@app/modules/auth";
import { Strings } from "@app/constants";
import { PATH_DASHBOARD } from "@app/routes/paths";
import { useDialog, useRequestState, useResponsive } from "@app/hooks";
import { selectMyProfileState } from "@app/modules/my-profile/selector";
import { selectIsApiRequest, selectIsAuthenticated } from "@app/modules/common";

export const useNavbarLogic = () => {
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const LoginDialogs = useDialog();
  const SignUpDialog = useDialog();
  const otpVerificationsDialogs = useDialog();

  const contactRef = useRef<HTMLDivElement | null>(null);
  const scrollBoxRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const theme = useTheme();
  const isMobile = useResponsive("down", "md");
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const { data } = useRequestState({ stateSelector: selectMyProfileState });
  const isLoading = useSelector(selectIsApiRequest);

  const handleLogoutClick = () => setOpenLogoutDialog(true);

  const handleConfirmLogout = () => {
    setOpenLogoutDialog(false);
    dispatch(logout());
  };

  const handleCancelLogout = () => setOpenLogoutDialog(false);

  const handlePopoverOpen = (event: any) => setAnchorEl(event.currentTarget);
  const handlePopoverClose = () => setAnchorEl(null);

  const scrollToTop = () => {
    scrollBoxRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const box = scrollBoxRef?.current;
    if (!box) return;
    const handleScroll = () => setShowScrollTop(box?.scrollTop > 300);
    box.addEventListener("scroll", handleScroll);
    return () => box?.removeEventListener("scroll", handleScroll);
  }, []);

  const defaultLinks = [
    {
      label: Strings.navBar.home,
      onClick: () => navigate(PATH_DASHBOARD.home),
      path: PATH_DASHBOARD.home,
    },
    {
      label: Strings.navBar.exploreCars,
      onClick: () =>
        navigate(PATH_DASHBOARD.productListPage, {
          state: { category: "New Cars" },
        }),
      path: PATH_DASHBOARD.productListPage,
    },
    // {
    //   label: Strings.navBar.testDrive,
    //   onClick: () => navigate(PATH_DASHBOARD.testDriveProcess),
    //   path: PATH_DASHBOARD.testDriveProcess,
    // },
    {
      label: Strings.navBar.contactUs,
      onClick: () =>
        contactRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        }),
      path: "#",
    },
    // {
    //   label: Strings.navBar.buyCars,
    //   onClick: () =>
    //     navigate(PATH_DASHBOARD.productListPage, {
    //       state: { category: "New Cars" },
    //     }),
    //   path: PATH_DASHBOARD.productListPage,
    // },
    {
      label: Strings.navBar.sellCars,
      onClick: () => navigate(PATH_DASHBOARD.sellCar),
      path: PATH_DASHBOARD.sellCar,
    },
    {
      label: Strings.navBar.beAnInvestor,
      onClick: () => navigate(PATH_DASHBOARD.beAnInvestor),
      path: PATH_DASHBOARD.beAnInvestor,
    },
  ];

  const sellCarOption = [
    {
      label: Strings.home.sellMyCar,
      value: Strings.home.sellMyCar,
      onClick: () => navigate(PATH_DASHBOARD.sellCar),
    },
    {
      label: Strings.home.checkCarValuation,
      value: Strings.home.checkCarValuation,
      onClick: () => navigate(PATH_DASHBOARD.checkValuation),
    },
  ];

  return {
    openLogoutDialog,
    handleLogoutClick,
    handleConfirmLogout,
    handleCancelLogout,
    contactRef,
    scrollBoxRef,
    showScrollTop,
    scrollToTop,
    anchorEl,
    handlePopoverOpen,
    handlePopoverClose,
    defaultLinks,
    sellCarOption,
    LoginDialogs,
    SignUpDialog,
    otpVerificationsDialogs,
    data,
    isLoading,
    isMobile,
    isAuthenticated,
    theme,
  };
};
